import { amountFormat, integerFormat, isEmpty } from '@/main';

export const AgGridDefineBuilder = {
  make(gridDefine, rows, $translator) {
    return {
      columnDefs: AgGridDefineBuilder.makeHeaders(gridDefine.fields, $translator),
      rowData: rows,
      defaultColDef: AgGridDefineBuilder.defaults(gridDefine.defaults),
      rowStyle: params => {
        if (params.node.rowPinned) {
          return { 'font-weight': 'bold' };
        }
      },
    };
  },
  makeHeaders(fields, $translator) {
    if (fields.fields) fields = fields.fields;

    return fields.map(field => {
      const fieldId = field.fieldId;
      let headerName = field.headerName || fieldId;
      if ($translator) {
        const translated = $translator(headerName);
        const isNotTranslated = translated.match(new RegExp(`^[A-Za-z0-9_\\-\\.]+\\.${headerName}$`));
        if (!isNotTranslated)
          headerName = translated;
      }
      const col = {
        field: field.fieldId,
        headerName: headerName,
        headerClass: 'customHeaderColor',
      };

      if (field.dataType === 'NUMERIC') {
        col.type = [ 'rightAligned' ];
        col.filter = 'agNumberColumnFilter';
        col.formatter = p => p.value > 0 ? amountFormat(p.value) : '';

      } else if (field.dataType === 'TEXT' && field.availableFilters && field.availableFilters.length > 0) {
        col.filter = 'agTextColumnFilter';
        if (field.availableFilters.find(flt => flt.filterType === 'CONTAINS'))
          col.filterParams = {
            'filterOptions': [
              'contains',
              'blank', 'notBlank',
            ],
          };

      } else if (field.dataType === 'DATE' && field.availableFilters && field.availableFilters.length > 0) {
        col.filter = 'agDateColumnFilter';
        col.filterParams = {
          'filterOptions': [
            'equals',
            'blank', 'notBlank',
          ],
        };

      } else {
        console.warn(field);
      }

      Object.keys(field).forEach(key => {
        if (field[key] !== undefined
          && [ 'fieldId', 'dataType', 'availableFilters', 'headerName', 'filterable', 'allowedValues' ].indexOf(key) < 0) {
          col[key] = field[key];
        }
      });
      if (field.filterValues)
        col.filterParams = { ...field.filterParams || {}, values: field.filterValues };

      return col;
    });
  },
  defaults(override) {
    return {
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      wrapText: true,
      autoHeight: false,
      width: undefined,
      ...override,
    };
  },
  initDefine(gridDefine) {
    gridDefine.withField = AgGridDefineBuilder.withField.bind(gridDefine);
    gridDefine.withFields = AgGridDefineBuilder.withFields.bind(gridDefine);
  },
  withField(fieldId, callback) {
    const field = this.fields.find(f => f.fieldId === fieldId);
    if (!field)
      console.warn(`Field ${fieldId} not found`);
    else
      callback(field);
  },
  withFields(fieldIds, callback) {
    fieldIds.forEach(fieldId => this.withField(fieldId, callback));
  },
};


export function reverseMapEnumFilter(field, values, reverseCallback, valueExtractor = v => v) {
  field.filter = 'agSetColumnFilter';
  field.filterValues = [];

  const reverseMapping = {};
  for (let i in values) {
    const reverse = reverseCallback(values[i]);
    if (field.filterValues.indexOf(reverse) < 0) {
      field.filterValues.push(reverse);
      reverseMapping[reverse] = [];
    }
    const value = valueExtractor(values[i]);
    reverseMapping[reverse].push(value);
  }

  // console.log(`Reverse mapping for field ${field.fieldId}:`, reverseMapping);
  return reverseMapping;
}

export function applyReverseMappedFilter(request, serverRequest, fieldId, mapping) {
  customHandleAgGridFilter(request, fieldId, filter => {
    // console.log(`Applying reverse mapped filter for field ${fieldId}:`, filter);
    const requestedServerSideValues = [];
    filter.values.forEach(translated => {
      if (translated === null)
        translated = '';
      const actualCodedValues = mapping[translated];
      // console.log(`Mapping filter value ${fieldId}`, translated, 'to', actualCodedValues);
      actualCodedValues && actualCodedValues.forEach(v => requestedServerSideValues.push(v));
    });

    if (requestedServerSideValues.length > 0)
      serverRequest.filters.push({ fieldId: fieldId, match: 'in', value: requestedServerSideValues });
  });
}

export function customHandleAgGridFilter(request, fieldId, customHandler) {
  if (request.filterModel[fieldId]) {
    const filter = request.filterModel[fieldId];
    filter._custom_handled = true;
    // delete request.filterModel[fieldId];
    customHandler(filter);
  }
}

export function fillAgGridFilters(request, serverRequest) {
  console.log(request.filterModel);
  if (!request.filterModel)
    return;

  Object
    .keys(request.filterModel)
    .filter(field => !request.filterModel[field]._custom_handled)
    .forEach(field => {
      const filter = request.filterModel[field];
      const converted = convertAgGridFilter(field, filter);
      if (converted)
        serverRequest.filters.push(converted);
    });
}

function convertAgGridFilter(fieldId, filter) {
  switch (filter.filterType) {
    case 'text':
      return {
        fieldId,
        match: mapAgGridFilterOperator(filter.type),
        value: isEmpty(filter.filter) ? '' : filter.filter,
      };
    case 'date':
      return {
        fieldId,
        match: mapAgGridFilterOperator(filter.type),
        value: isEmpty(filter.dateFrom) ? '' : filter.dateFrom,
      };
    case 'number': {
      if (filter.type === 'inRange') {
        return { fieldId, match: 'rng', value: filter.filter, valueTo: filter.filterTo };
      } else
        return { fieldId, match: mapAgGridFilterOperator(filter.type), value: filter.filter };
    }
    case 'set': {
      if (filter.values && filter.values.length > 0)
        return { fieldId, match: 'in', value: filter.values };
      else
        return null;
    }
    default:
      console.warn('Unmanaged Filter', filter);
      return { fieldId, match: 'eq', value: filter.filter };
  }
}

function mapAgGridFilterOperator(type) {
  const mapping = {
    'equals': 'eq',
    'lessThan': 'lt',
    'lessThanOrEqual': 'lte',
    'greaterThan': 'gt',
    'greaterThanOrEqual': 'gte',
    'blank': 'isNull',
    'notBlank': 'notNull',
  };
  return mapping[type] || type;
}

export const normalizeEmptyValue = (value) => {
  return (value === null || value === undefined || value === '') ? null : value;
};

export function deleteIfEmpty(object, key) {
  if (object[key] && Object.keys(object[key]).length === 0) {
    delete object[key];
  }
}

export function enrichBuyerData(buyer, fiscalDataMap, customDataMap) {
  buyer.buyerCustomData = customDataMap ? customDataMap[buyer.id] : {};
  if (isEmpty(buyer.buyerCustomData))
    buyer.buyerCustomData = {};

  const fiscalData = fiscalDataMap ? fiscalDataMap[buyer.id] : null;
  if (isEmpty(fiscalData)) {
    buyer.buyerFiscalData = [];
  } else {
    buyer.buyerFiscalData = fiscalData.fiscalData;
    buyer.mainFiscalRef = fiscalData.mainFiscalRef;
  }

  return buyer;
}


//#region Analisi Aggregate Rischi
export function rowTypeFormatter(params) {
  if (!params.value && params.value !== 0) return '';

  switch (params.data.rowType) {
    case 0: // Counter - intero
      return integerFormat(params.value);
    case 1: // Economic - migliaia.decimali,xx
      return amountFormat(params.value);
    case 2: // Percentage - decimali,x
      return new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(params.value);
    default:
      return params.value;
  }
}

export function applyRowTypeTransformation(categories, translations) {
  categories.forEach((item, index) => {
    item.rowType = index % 3; // 0=Counter, 1=Economic, 2=Percentage

    const translation = translations[item.rowType];
    if (typeof translation === 'function') {
      item.category = translation(item);
    } else {
      item.category = translation;
    }
  });

  return categories;
}

export function aggregateRiskCalculateTotals(analysis, $t) {
  if (analysis.rowData.length <= 0) {
    delete analysis.totals;
    return;
  }
  const fstRow = analysis.rowData[0];

  const totals = {};
  totals[Object.keys(fstRow)[0]] = $t('riskgeographic.totals');

  const keys = Object.keys(fstRow).filter(k => k !== 'areaCode' && k !== 'category');
  keys.forEach(k => totals[k] = 0);

  analysis.rowData.forEach(r => {
    keys.filter(k => !isNaN(r[k]))
      .forEach(k => totals[k] += r[k]);
  });
  if (totals.total > 100)
    totals.total = 100;
  keys.filter(k => !isNaN(totals[k]))
    .forEach(k => totals[k] = totals[k].toFixed(1));

  analysis.totals = [ totals ];
}

//#endregion
